// AgentsPage — 2-pane workspace browser. Left rail lists the workspace's
// agents; clicking a row swaps the center pane to that agent's detail view.
// Mirrors the layout pattern from EvaluationTemplatesPage / GraderPage.

import {
  Badge,
  Body1,
  Button,
  Spinner,
  Tab,
  TabList,
  mergeClasses,
} from '@fluentui/react-components';
import { Add24Filled, Add24Regular, bundleIcon } from '@fluentui/react-icons';
import type { ReactElement } from 'react';
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMatch, useNavigate, useOutlet, useParams } from 'react-router';

import { useWorkspaceContext } from '../../context/WorkspaceContext.js';
import {
  DataLakeEntitlementGuidance,
  isDataLakeEntitlementRequiredMessage,
} from '../../features/private-workspaces/index.js';
import { useAgents } from '../../hooks/useAgents/useAgents.js';
import { useWorkspaces } from '../../hooks/useWorkspaces/useWorkspaces.js';
import type { Agent } from '../../types/agent.js';

import { AgentDetailView } from './AgentDetailView/AgentDetailView.js';
import type { AgentFormOutletContext } from './AgentForm/AgentForm.js';
import { useStyles } from './AgentsPage.style.js';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
const AddIcon = bundleIcon(Add24Filled, Add24Regular);
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

type FilterValue = 'all' | 'active' | 'draft';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface RowProps {
  agent: Agent;
  selected: boolean;
  onSelect: (name: string) => void;
}

function AgentRow({ agent, selected, onSelect }: RowProps) {
  const styles = useStyles();

  return (
    <button
      type="button"
      className={mergeClasses(styles.row, selected && styles.rowSelected)}
      onClick={() => onSelect(agent.name)}
      aria-pressed={selected}
      aria-label={`Open agent ${agent.name}`}
    >
      <div className={styles.rowNameLine}>
        <span className={styles.rowName}>{agent.name}</span>
      </div>
      <span className={styles.rowMeta}>
        Created {formatDate(agent.systemProperties.createdDate)}
      </span>
    </button>
  );
}

export function AgentsPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { workspaceName: workspaceNameParam } = useParams<{
    workspaceName: string;
  }>();
  const { activeName } = useWorkspaceContext();

  // When the user switches workspace via the switcher, navigate to the new
  // workspace's agents URL.
  useEffect(() => {
    if (activeName && workspaceNameParam && activeName !== workspaceNameParam) {
      void navigate(`/workspaces/${encodeURIComponent(activeName)}/agents`, {
        replace: true,
      });
    }
  }, [activeName, workspaceNameParam, navigate]);

  const { activeWorkspace, isLoading: workspacesLoading } = useWorkspaces();
  const workspaceName = activeWorkspace?.name ?? null;

  const basePath = `/workspaces/${encodeURIComponent(workspaceNameParam!)}/agents`;

  const isCreating = useMatch('/workspaces/:workspaceName/agents/new') !== null;
  const detailMatch = useMatch('/workspaces/:workspaceName/agents/:agentName');
  const routeAgentName =
    detailMatch?.params.agentName !== undefined &&
    detailMatch.params.agentName !== 'new'
      ? detailMatch.params.agentName
      : null;

  const { agents, isLoading, error, refresh, upsertAgent } = useAgents({
    workspaceName,
  });

  const [filter, setFilter] = useState<FilterValue>('all');

  const filteredAgents = useMemo(() => {
    switch (filter) {
      case 'active':
        return agents.filter((a) => !a.isDraft);
      case 'draft':
        return agents.filter((a) => a.isDraft);
      default:
        return agents;
    }
  }, [agents, filter]);

  // The route param (:agentName) is the single source of truth for the
  // selected agent. When the URL points at no / an unknown / a filtered-out
  // agent, fall back to the first visible row so the center pane always has
  // something to show; an effect then syncs that choice back into the URL.
  const selectedName = useMemo<string | null>(() => {
    if (
      routeAgentName !== null &&
      filteredAgents.some((a) => a.name === routeAgentName)
    ) {
      return routeAgentName;
    }
    return filteredAgents[0]?.name ?? null;
  }, [routeAgentName, filteredAgents]);

  // Keep the URL in step with the effective selection. Because the :agentName
  // child route has no component of its own, this navigation re-matches without
  // remounting AgentsPage. Skipped while creating (the /new outlet owns the
  // pane) or before the list has loaded.
  useEffect(() => {
    if (isCreating || isLoading) return;
    if (selectedName === routeAgentName) return;
    const target =
      selectedName === null
        ? basePath
        : `${basePath}/${encodeURIComponent(selectedName)}`;
    void navigate(target, { replace: true });
  }, [isCreating, isLoading, selectedName, routeAgentName, basePath, navigate]);

  const listSelectedName = isCreating ? null : selectedName;

  // Defer the (heavier) detail render so row highlighting stays snappy.
  const detailSelectedName = useDeferredValue(selectedName);

  const handleRowSelect = useCallback(
    (name: string) => {
      void navigate(`${basePath}/${encodeURIComponent(name)}`, {
        replace: true,
      });
    },
    [navigate, basePath],
  );

  const goToCreate = useCallback(() => {
    void navigate(`${basePath}/new`);
  }, [navigate, basePath]);

  const handleDeleted = useCallback(() => {
    void navigate(basePath, { replace: true });
    refresh();
  }, [navigate, basePath, refresh]);

  const handleAgentCreated = useCallback(
    (agent: Agent) => {
      upsertAgent(agent);
    },
    [upsertAgent],
  );
  const childOutlet = useOutlet({
    onCreated: handleAgentCreated,
  } satisfies AgentFormOutletContext) as ReactElement | null;

  if (workspacesLoading) {
    return (
      <div className={styles.fullPagePlaceholder}>
        <Spinner label="Loading workspaces..." />
      </div>
    );
  }

  if (workspaceName === null) {
    return (
      <div className={styles.fullPagePlaceholder}>
        <Body1>Pick a workspace from the top bar to view its agents.</Body1>
      </div>
    );
  }

  let listBody: React.ReactNode;
  if (error !== null) {
    const isEntitlementRequired = isDataLakeEntitlementRequiredMessage(error);
    listBody = (
      <div className={styles.fullPagePlaceholder}>
        {isEntitlementRequired ? (
          <DataLakeEntitlementGuidance />
        ) : (
          <Body1 className={styles.errorMessage}>{error}</Body1>
        )}
        <Button onClick={refresh}>Try again</Button>
      </div>
    );
  } else if (agents.length === 0 && !isCreating && !isLoading) {
    listBody = null;
  } else {
    listBody = (
      <div className={styles.list}>
        {isCreating && (
          <div
            className={mergeClasses(styles.row, styles.rowNew)}
            aria-hidden="true"
          >
            <div className={styles.rowNameLine}>
              <span className={styles.rowName}>New agent</span>
              <Badge size="small" appearance="tint" color="brand">
                New
              </Badge>
            </div>
            <span className={styles.rowMeta}>
              <em>Editing…</em>
            </span>
          </div>
        )}
        {filteredAgents.map((a) => (
          <AgentRow
            key={a.name}
            agent={a}
            selected={a.name === listSelectedName}
            onSelect={handleRowSelect}
          />
        ))}
      </div>
    );
  }

  let centerBody: React.ReactNode;
  if (isCreating) {
    centerBody = childOutlet;
  } else if (isLoading && agents.length === 0) {
    centerBody = (
      <div className={styles.fullPagePlaceholder}>
        <Spinner label="Loading agents..." />
      </div>
    );
  } else if (error === null && !isLoading && agents.length === 0) {
    centerBody = (
      <div className={styles.centerEmpty}>
        <span className={styles.centerEmptyTitle}>
          No agents in this workspace yet
        </span>
        <span className={styles.centerEmptySub}>
          Create one to start evaluating it against your graders.
        </span>
        <Button appearance="primary" icon={<AddIcon />} onClick={goToCreate}>
          Create Agent
        </Button>
      </div>
    );
  } else if (detailSelectedName === null) {
    centerBody = (
      <div className={styles.centerEmpty}>
        <span className={styles.centerEmptyTitle}>Select an agent</span>
        <span className={styles.centerEmptySub}>
          Choose an agent from the list, or create a new one to get started.
        </span>
      </div>
    );
  } else {
    centerBody = (
      <AgentDetailView
        workspaceName={workspaceName}
        agentName={detailSelectedName}
        onDeleted={handleDeleted}
        onChanged={refresh}
      />
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.panelsRow}>
        {/* ─── Left pane: list ─────────────────────────────────────────── */}
        <div className={styles.leftPanel}>
          {agents.length > 0 && (
            <div className={styles.filterTabs}>
              <TabList
                size="small"
                selectedValue={filter}
                onTabSelect={(_, d) => setFilter(d.value as FilterValue)}
              >
                <Tab value="all">All</Tab>
                <Tab value="active">Active</Tab>
                <Tab value="draft">Draft</Tab>
              </TabList>
            </div>
          )}

          {listBody}

          <div className={styles.leftFooter}>
            <Button
              appearance="primary"
              icon={<AddIcon />}
              className={styles.fullWidthButton}
              onClick={goToCreate}
            >
              Create Agent
            </Button>
          </div>
        </div>

        {/* ─── Center pane: detail ─────────────────────────────────────── */}
        <div className={styles.centerPanel}>{centerBody}</div>
      </div>
    </div>
  );
}
